import { useEffect, useRef, useState } from "react";
import { TerminalSquare } from "lucide-react";
import { useI18n } from "@/i18n";

const buildScript = (lang) => {
  const en = lang === "en";
  return [
    { cmd: "ssh root@pbs-01.dmo-tech.ro", out: [{ t: en ? "Proxmox Backup Server 3.2 — authenticated" : "Proxmox Backup Server 3.2 — autentificat", c: "cyan" }] },
    { cmd: "proxmox-backup-client backup vm/101 --ns prod", out: [{ t: en ? "✔ backup complete · 0 errors · 2m14s" : "✔ backup complet · 0 erori · 2m14s", c: "ok" }] },
    { cmd: "zabbix_get -s 10.10.0.5 -k system.uptime", out: [{ t: en ? "➜ 41 days, 07:12:36" : "➜ 41 zile, 07:12:36", c: "cyan" }] },
    { cmd: "pfctl -s rules | grep block", out: [{ t: en ? "➜ 342 active rules" : "➜ 342 reguli active", c: "cyan" }, { t: en ? "⚠ 48,254 packets blocked in 24h" : "⚠ 48.254 pachete blocate în 24h", c: "warn" }] },
    { cmd: "docker compose up -d", out: [{ t: en ? "✔ 6/6 containers started" : "✔ 6/6 containere pornite", c: "ok" }] },
    { cmd: "zpool status tank", out: [{ t: en ? "➜ state: ONLINE · 0 errors · scrub OK" : "➜ state: ONLINE · 0 erori · scrub OK", c: "ok" }] },
    { cmd: "wg show wg0 latest-handshakes", out: [{ t: en ? "● tunnel up · 3 peers up" : "● tunel activ · 3 peers up", c: "ok" }] },
    { cmd: "curl -s https://app.dmo-tech.ro/health", out: [{ t: '{ "status": "ok", "uptime": "99.98%" }', c: "cyan" }] },
  ];
};

const COLOR = {
  cmd: "text-white",
  ok: "text-emerald-400",
  warn: "text-amber-400",
  cyan: "text-cyan-neon",
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const LiveTerminal = () => {
  const { t, lang } = useI18n();
  const [lines, setLines] = useState([]);
  const [typing, setTyping] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let out = [];
    let si = 0;
    const SCRIPT = buildScript(lang);
    const push = (line) => {
      out = [...out.slice(-11), line];
      setLines(out);
    };
    async function run() {
      await wait(600);
      while (!cancelled) {
        const step = SCRIPT[si % SCRIPT.length];
        si += 1;
        const full = "$ " + step.cmd;
        for (let i = 1; i <= full.length; i++) {
          if (cancelled) return;
          setTyping(full.slice(0, i));
          await wait(26);
        }
        if (cancelled) return;
        push({ t: full, c: "cmd" });
        setTyping("");
        await wait(380);
        for (const o of step.out) {
          if (cancelled) return;
          push(o);
          await wait(520);
        }
        await wait(950);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [lang]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines, typing]);

  return (
    <div data-testid="live-terminal" className="card-edge overflow-hidden rounded-xl">
      <div className="flex items-center gap-2 border-b border-white/10 bg-ink-900/60 px-4 py-2.5">
        <span className="h-3 w-3 rounded-full bg-[#ff5f56]" />
        <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
        <span className="h-3 w-3 rounded-full bg-[#27c93f]" />
        <div className="ml-2 flex items-center gap-2 text-white/50">
          <TerminalSquare size={14} />
          <span className="font-mono text-[11px] tracking-wide">root@dmo-noc — zsh</span>
        </div>
        <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-emerald-400">● {t("term.status")}</span>
      </div>
      <div ref={bodyRef} className="h-56 overflow-hidden bg-[#0A0F16] px-4 py-3 font-mono text-[12px] leading-relaxed sm:text-[13px]">
        {lines.map((l, i) => (
          <div key={i} className={COLOR[l.c] || "text-white/70"}>{l.t}</div>
        ))}
        {typing && (
          <div className="text-white">
            {typing}
            <span className="ml-0.5 inline-block h-3.5 w-2 translate-y-0.5 animate-pulse bg-cyan-neon" />
          </div>
        )}
      </div>
    </div>
  );
};
